import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { merchantApi } from '../api/config';
import { useAuth } from './AuthContext';

interface WalletData {
  balance: number;
  payout_balance: number;
  available_balance?: number;
  is_frozen?: boolean;
  total_credit?: number;
  total_debit?: number;
  total_payout_credit?: number;
  total_payout_debit?: number;
  frozen_amount?: number;
  frozen_payin?: number;
  frozen_payout?: number;
  freeze_date?: string;
  [key: string]: any;
}

interface WalletContextType {
  wallet: WalletData | null;
  walletLoading: boolean;
  loadWallet: () => Promise<void>;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export const WalletProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadWallet = useCallback(async () => {
    try {
      const res = await merchantApi.get('/merchants/wallet');
      setWallet(res.data.data);
    } catch {
      // silent
    } finally {
      setWalletLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setWalletLoading(false);
      return;
    }

    loadWallet();
    intervalRef.current = setInterval(loadWallet, 30000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAuthenticated, loadWallet]);

  return (
    <WalletContext.Provider value={{ wallet, walletLoading, loadWallet }}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error('useWallet must be used within WalletProvider');
  return ctx;
};
